// fc-cdf3bdad postmortem: GlobalErrorHandler para capturar excepciones no
// manejadas de Angular y reportarlas al BE vía WS como métrica.
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { FcSessionService } from '../components/pages/fc-session/fc-session.service';
import { isApiError } from '../shared/utils/rum-error.util';
import { isRumReady } from '../shared/utils/rum-user.util';

const DEDUP_WINDOW_MS = 10_000;
const MAX_REPORTS_PER_MINUTE = 20;

@Injectable()
export class FcErrorHandler implements ErrorHandler {
  private readonly lastReportedAt = new Map<string, number>();
  private minuteStart = 0;
  private minuteCount = 0;

  constructor(private injector: Injector) {}

  handleError(error: any): void {
    // An unhandled ApiError was already reported by the HTTP interceptor.
    if (isApiError(error)) {
      console.warn('[FC-GLOBAL-ERROR] unhandled api_error', error.status);
      return;
    }

    const err = error?.rejection ?? error; // zone.js wraps promise rejections
    const e = err instanceof Error ? err : new Error(String(err));
    if (isRumReady()) {
      if (this.shouldReport(e.message)) datadogRum.addError(e, { handler: 'angular_error_handler' });
      console.warn('[FC-GLOBAL-ERROR]', e);
    } else {
      console.error('[FC-GLOBAL-ERROR]', e);
    }

    // Best-effort: enviar al BE vía WS si está abierto
    try {
      this.injector.get(FcSessionService).sendClientMetric('uncaught_error', 1, 'count');
    } catch {
      // Silent — no queremos cascading errors
    }
  }

  /** Same message at most once per 10 s, and at most 20 reports per minute: a render loop must not flood RUM. */
  private shouldReport(message: string): boolean {
    const now = Date.now();
    const last = this.lastReportedAt.get(message);
    if (last !== undefined && now - last < DEDUP_WINDOW_MS) return false;
    if (now - this.minuteStart >= 60_000) {
      this.minuteStart = now;
      this.minuteCount = 0;
    }
    if (this.minuteCount >= MAX_REPORTS_PER_MINUTE) return false;
    this.minuteCount++;
    if (this.lastReportedAt.size >= 100) this.lastReportedAt.clear();
    this.lastReportedAt.set(message, now);
    return true;
  }
}
